import "./App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useParams, Link, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Header from "./components/Header";
import Hero from "./components/Hero";
import {
  HowItWorks,
  WhyWorks,
  Flow,
  WorkoutsSupport,
  LongevityStrength,
  StartPlan,
  ImportantNote,
} from "./components/Sections";
import IdeasCarousel from "./components/IdeasCarousel";
import Pricing from "./components/Pricing";
import FounderStrip from "./components/FounderStrip";
import Footer from "./components/Footer";
import MyPlan from "./pages/MyPlan";
import Settings from "./pages/Settings";
import Studio from "./pages/Studio";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import PaymentSuccess from "./pages/PaymentSuccess";
import HowItWorksPage from "./pages/HowItWorks";
import RefBanner from "./components/RefBanner";
import CookieBanner from "./components/CookieBanner";
import SEO, { softwareAppJsonLd } from "./components/SEO";
import FeaturePage, { FEATURE_SLUGS } from "./pages/FeaturePage";
import { Toaster } from "./components/ui/toaster";
import { SubscriptionProvider } from "./context/SubscriptionContext";
import RequirePro from "./components/RequirePro";
import { captureRefFromUrl } from "./lib/referral";
import { captureFirstTouch } from "./lib/attribution";
import { trackEvent, trackPageView } from "./lib/analytics";

/**
 * Fires a GA4 page_view on every React Router navigation. SPAs need this
 * because gtag('config') is only called once at load — subsequent history
 * pushes don't automatically re-trigger a page_view unless we do it manually.
 */
const RouteTracker = () => {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);
  return null;
};

const HOME_FAQ = [
  { q: "What makes FreeFitFuel different from other fitness apps?",
    a: "It's the only AI fitness tutor that adapts to you across three axes at once — your body (injuries, energy, recovery), your kit (whatever equipment you own), and your goals. Every workout is regenerated for the person you are today, not the person you signed up as." },
  { q: "Do I need a gym or equipment?",
    a: "No. Tell us what you own — anything from just bodyweight to a full commercial gym — and the plan uses only what you have. Change it any time." },
  { q: "How does the injury adaptation work?",
    a: "Log any injury (shoulder, knee, back, wrist, etc.) and a deterministic safety pass removes every contraindicated exercise from your future workouts, swapping them for joint-friendly alternatives that still train the same muscle." },
  { q: "Is the meal planner UK-specific?",
    a: "Yes. Plans are priced against real UK supermarket data (Tesco, Sainsbury's, ASDA, Waitrose, Aldi, Lidl, Morrisons, Booths) with an aisle-sorted shopping list you can copy or download." },
  { q: "How much does it cost?",
    a: "£5/month, £25/year, or £19/year forever for founding members. Cancel any time." },
];

const homeFaqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: HOME_FAQ.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
};

const Home = () => {
  useEffect(() => { trackEvent("home_view"); }, []);
  return (
    <div className="min-h-screen bg-[#0b0b0c]">
      <SEO
        title=""
        description="FreeFitFuel — the AI fitness tutor that adapts as your body does. Personalised training, nutrition and recovery guidance that dynamically responds to your injuries, equipment, goals, progress and recovery."
        path="/"
        keywords="ai fitness app, personalised training, macro meal planner, injury adaptive workouts, uk fitness app"
        jsonLd={{
          '@context': 'https://schema.org',
          '@graph': [
            softwareAppJsonLd({
              name: "FreeFitFuel",
              description: "AI fitness tutor with adaptive training, meal planning and injury-aware progressions.",
              url: "https://freefitfuel.app",
            }),
            homeFaqJsonLd,
          ],
        }}
      />
      <FounderStrip />
      <Header />
      <main>
        <Hero />
        <Pricing />
        <HowItWorks />
        <WhyWorks />
        <Flow />
        <WorkoutsSupport />
        <LongevityStrength />
        <IdeasCarousel />
        <StartPlan />
        <ImportantNote />
      </main>
      <Footer />
    </div>
  );
};

const FeaturesIndex = () => (
  <div className="min-h-screen bg-[#0b0b0c] text-white">
    <SEO
      title="Features"
      description="Every FreeFitFuel capability — adaptive training, AI meal planning, food snap, injury-aware workouts, progression tracking and an AI coach."
      path="/features"
      keywords="freefitfuel features, ai fitness app features"
    />
    <Header />
    <main className="max-w-3xl mx-auto px-5 py-16 sm:py-24">
      <p className="text-[11px] uppercase tracking-[0.2em] font-black text-[#BEFF38]">Every capability</p>
      <h1 className="mt-3 text-4xl sm:text-5xl font-extrabold tracking-tight">What FreeFitFuel does</h1>
      <p className="mt-4 text-lg text-zinc-400 leading-relaxed">Six built-in tools working together — pick any one to dig into how it adapts to you.</p>
      <div className="mt-10 grid sm:grid-cols-2 gap-3">
        {FEATURE_SLUGS.map((slug) => (
          <Link
            key={slug}
            to={`/features/${slug}`}
            data-testid={`features-index-${slug}`}
            className="block p-5 rounded-xl border border-zinc-800 bg-zinc-950 hover:border-[#BEFF38]/50 hover:bg-zinc-900 transition-colors"
          >
            <span className="text-[10px] uppercase tracking-wider font-black text-[#ff7a1a]">/features/{slug}</span>
            <p className="mt-1 text-white font-bold capitalize">{slug.replace(/-/g, " ")}</p>
          </Link>
        ))}
      </div>
    </main>
    <Footer />
  </div>
);

const FeatureRoute = () => {
  const { slug } = useParams();
  if (!FEATURE_SLUGS.includes(slug)) return <FeaturesIndex />;
  return (
    <>
      <Header />
      <FeaturePage slug={slug} />
      <Footer />
    </>
  );
};

function App() {
  useEffect(() => { captureRefFromUrl(); captureFirstTouch(); trackEvent("app_open"); }, []);
  return (
    <div className="App">
      <HelmetProvider>
        <SubscriptionProvider>
          <BrowserRouter>
            <RouteTracker />
            <RefBanner />
            <CookieBanner />
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/features" element={<FeaturesIndex />} />
              <Route path="/features/:slug" element={<FeatureRoute />} />
              <Route path="/how-it-works" element={<HowItWorksPage />} />
              <Route path="/my-plan" element={<RequirePro><MyPlan /></RequirePro>} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/studio" element={<Studio />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/payment-success" element={<PaymentSuccess />} />
            </Routes>
          </BrowserRouter>
          <Toaster />
        </SubscriptionProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
